import React, { useState, useEffect, useCallback, useRef } from 'react';
import { nfConfirm } from '../components/confirm';
import { api } from '../api';
import NumInput from '../components/NumInput';
import Toggle from '../components/Toggle';
import { useCatalogLabels } from '../catalogLabels';

// Подписи для карточек и журнала (значения приходят с бэка как есть)
const KIND_LABELS = { product: 'Стебель', bouquet: 'Букет', postcard: 'Открытка' };
const MODE_LABELS = { stock: 'В наличии', production: 'Под заказ', hidden: 'Скрыта' };
const LOG_KIND_LABELS = { catalog: 'Каталог', order: 'Заказы', webhook: 'Вебхук' };
// Подкатегории Flowwow, которые назначаем сами (синхронно с flowwow_catalog.SUBCAT_NAMES)
const SUBCAT_LABELS = { 118: 'Цветы поштучно', 1: 'Монобукеты', 2: 'Авторские букеты', 3: 'Цветы в коробке', 4: 'Цветы в корзине', 7: 'Композиции из цветов' };
const subcatLabel = (id) => (id == null ? '—' : (SUBCAT_LABELS[id] ? `${SUBCAT_LABELS[id]} (${id})` : String(id)));
const PLAN_LABELS = {
  create: 'создать', update: 'обновить', stocks: 'остатки', prices: 'цены', unhide: 'показать', hide: 'скрыть',
};
// productionTime.measure из API Flowwow: 8 — часы, 9 — дни
const MEASURE_LABELS = { 8: 'ч', 9: 'дн' };

// Зоны своей доставки для кабинета Flowwow из нашего тарифа. Зона Flowwow — радиус от
// магазина (по прямой), наш тариф — км по дорогам: радиус = км / 1,3 (вниз до 0,5 км),
// чтобы покупатель на площадке платил не меньше нашего тарифа. Цена + ~3 % — столько
// Flowwow удерживает с доставки (2,5 % + НДС 22 %). Зон в кабинете не больше семи.
const FW_ZONES_MAX = 7;
const ROAD_FACTOR = 1.3;
const DELIVERY_HOLD = 0.025 * 1.22;
function flowwowZones(intervals) {
  const rows = (intervals || []).map(it => ({
    ourKm: it.up_to_km,
    ourPrice: it.price,
    radius: Math.max(0.5, Math.floor((it.up_to_km / ROAD_FACTOR) * 2) / 2),
    fwPrice: Math.ceil(it.price / (1 - DELIVERY_HOLD) / 10) * 10,
  }));
  // Мелкие интервалы дают один радиус — в кабинет две зоны «до 1,5 км» не внести:
  // оставляем строку с бóльшей ценой (последнюю), потом режем до семи
  const zones = [];
  for (const r of rows) {
    if (zones.length && zones[zones.length - 1].radius === r.radius) zones[zones.length - 1] = r;
    else zones.push(r);
  }
  return { zones: zones.slice(0, FW_ZONES_MAX), more: zones.length > FW_ZONES_MAX };
}
const fmtKm = (v) => String(v).replace('.', ',');

// Настройки Flowwow сохраняются сами (решение 09.10: кнопка «Сохранить» не нужна) — ключ
// страницы → значение для PUT /flowwow/settings (ключ API — flowwow_<ключ>)
const intOr = (d) => (v) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : d; };
const SETTING_API = {
  sync_enabled: (v) => !!v, publish_stems: (v) => !!v, publish_bouquets: (v) => !!v, auto_accept: (v) => !!v,
  top18_auto: (v) => !!v, delivery_own: (v) => !!v, delivery_courier: (v) => !!v, self_delivery: (v) => !!v,
  delivery_pickup: (v) => !!v, recipe_bouquets_stock: (v) => !!v,
  markup_percent: (v) => Number(v) || 0,
  // пусто — как общая комиссия
  markup_percent_stems: (v) => {
    const t = String(v ?? '').trim().replace(',', '.');
    const n = parseFloat(t);
    return t === '' || !Number.isFinite(n) ? '' : Math.min(90, Math.max(0, n));
  },
  postcard_image_url: (v) => (v || '').trim(),
  default_stem_height_cm: intOr(1), default_bouquet_width_cm: intOr(1), default_bouquet_height_cm: intOr(1),
  bouquet_production_hours: intOr(1), min_days_left: intOr(0), planned_today_lead_hours: intOr(1),
  top18_bouquets: (v) => Math.min(18, Math.max(0, parseInt(v, 10) || 0)),
  // пусто — 80 км по умолчанию
  address_radius_km: (v) => {
    const n = parseInt(String(v ?? '').trim(), 10);
    return Number.isFinite(n) && n > 0 ? Math.min(500, n) : '';
  },
};

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function statusBadge(status) {
  const cls = status === 'ok' ? 'completed' : status === 'error' ? 'cancelled' : 'pending';
  const label = status === 'ok' ? 'ок' : status === 'error' ? 'ошибка' : (status || '—');
  return <span className={`badge badge-${cls}`}>{label}</span>;
}

/** Состояние карточки одним словом для продавца */
// Видна ли карточка покупателям прямо сейчас — факт, а не настройка: решает пауза,
// «Продаём», остаток, фото и модерация площадки. Почему не видна — колонка рядом.
function offerState(o) {
  if (o.last_error) return { cls: 'cancelled', label: 'Ошибка', visible: false };
  if (o.is_published && !o.is_hidden_remote && o.remote_active !== false) return { cls: 'completed', label: 'Видна', visible: true };
  return { cls: 'inactive', label: 'Не видна', visible: false };
}

const normName = (v) => (v || '').toLowerCase().replace(/ё/g, 'е');

// Топ-18: 0 — закреплена, 1 — в топе автоматически, 2 — не в топе
const topRank = (o) => (o.top_pinned ? 0 : o.in_top ? 1 : 2);

// Сортировка по клику на заголовок, как в каталоге. asc: А→Я, продаём/видна — сначала,
// остаток и цена — по возрастанию; повторный клик разворачивает.
function sortOffers(rows, field, dir) {
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'ru');
  const num = (v) => (v == null || v === '' ? -1 : Number(v));
  const key = {
    name: () => 0,
    here: (a, b) => (b.shown_here ? 1 : 0) - (a.shown_here ? 1 : 0),
    visible: (a, b) => (offerState(b).visible ? 1 : 0) - (offerState(a).visible ? 1 : 0),
    // asc: закреплённые, потом в топе автоматически, потом остальные
    top: (a, b) => topRank(a) - topRank(b),
    stock: (a, b) => num(a.last_stock) - num(b.last_stock),
    price: (a, b) => num(a.last_price) - num(b.last_price),
  }[field] || (() => 0);
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => sign * (key(a, b) || byName(a, b)));
}

function SortIcon({ active, dir }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, verticalAlign: 'middle', opacity: active ? 1 : 0.35 }}>
      {!active ? (<><polyline points="7 15 12 20 17 15" /><polyline points="7 9 12 4 17 9" /></>)
        : dir === 'asc' ? <polyline points="7 14 12 9 17 14" /> : <polyline points="7 10 12 15 17 10" />}
    </svg>
  );
}

function FlowwowPage({ showToast }) {
  const catalogLabels = useCatalogLabels();
  const [offerQuery, setOfferQuery] = useState('');
  const [offerSearchOpen, setOfferSearchOpen] = useState(false);
  const [offerSort, setOfferSort] = useState({ field: 'name', dir: 'asc' });
  const [offerKind, setOfferKind] = useState('product');   // вкладка «Стебли» / «Букеты»
  const sortBy = (field) => setOfferSort(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  // Последние сохранённые значения: поле сохраняем, только если оно действительно поменялось
  const savedRef = useRef(null);
  const [tariff, setTariff] = useState(null);   // интервалы доставки — для подсказки зон Flowwow
  const [tariffError, setTariffError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('settings');
  const [busy, setBusy] = useState(null); // 'sync' | 'full' | 'poll' | 'save' | 'pause'
  const [actionResult, setActionResult] = useState(null); // { title, data }

  const [offers, setOffers] = useState(null);
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState(null);
  const [logKind, setLogKind] = useState('');
  const [foreign, setForeign] = useState(null);
  const [foreignSelected, setForeignSelected] = useState(new Set());
  const [foreignLinks, setForeignLinks] = useState({});   // productId карточки → id нашего букета
  const [tabLoading, setTabLoading] = useState(false);
  // Служебный режим — для разработчика платформы: кнопки синка/сверки/опроса, журнал,
  // предпросмотр, чужие карточки, расширенные настройки. Продавцу — статус, пауза,
  // настройки, карточки с причинами.
  const [advanced, setAdvanced] = useState(() => { try { return localStorage.getItem('flowwow_advanced') === '1'; } catch { return false; } });
  const toggleAdvanced = () => { const v = !advanced; setAdvanced(v); try { localStorage.setItem('flowwow_advanced', v ? '1' : '0'); } catch {} };

  const loadStatus = useCallback(async (silent = false) => {
    try {
      const data = await api.getFlowwowStatus();
      setStatus(data);
      if (data.settings) { setSettings(data.settings); savedRef.current = data.settings; }
    } catch (error) {
      if (!silent) showToast(error.message || 'Ошибка загрузки статуса Flowwow', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadOffers = useCallback(async () => {
    setTabLoading(true);
    try { setOffers(await api.getFlowwowOffers()); }
    catch (error) { showToast(error.message || 'Ошибка загрузки карточек', 'error'); }
    finally { setTabLoading(false); }
  }, [showToast]);

  // Переключатель «На Flowwow»: положение = видна ли карточка покупателям сейчас.
  // Выключили — позиция снимается с Flowwow вручную (у нас продаётся дальше).
  // Включили — снимаем ручной запрет; если показать всё равно нельзя (остаток, фото,
  // модерация, пауза), переключатель вернётся в «выкл» и подскажет почему — как «Виден»
  // в каталоге без остатка. Пока синк отрабатывает (пара секунд), держим желаемое положение.
  const [pendingFw, setPendingFw] = useState({});
  const rowKey = (o) => `${o.kind}-${o.entity_id}`;
  const whyHidden = (o) => o.last_error || o.reason
    || (o.remote_active === false ? 'модерация Flowwow или нет фото — см. кабинет Flowwow' : 'карточка ещё не выгружена');
  const refetchRow = async (o) => {
    const data = await api.getFlowwowOffers();
    setOffers(data);
    return data.items.find(x => x.kind === o.kind && x.entity_id === o.entity_id);
  };

  const toggleFlowwow = async (o) => {
    const key = rowKey(o);
    const current = pendingFw[key] ?? offerState(o).visible;
    const wants = !current;
    if (wants && !o.flowwow_excluded) {
      // Вручную не снимали — включать нечего, мешает другое
      showToast(`«${o.name}» на Flowwow сейчас не показать: ${whyHidden(o)}. Появится сама, когда причина уйдёт`, 'error');
      return;
    }
    setPendingFw(prev => ({ ...prev, [key]: wants }));
    setBusy(`fw-${key}`);
    try {
      await api.setFlowwowExcluded(o.kind, o.entity_id, !wants);
      showToast(wants ? `«${o.name}» возвращаем на Flowwow` : `«${o.name}» снята с Flowwow — у вас продаётся дальше`);
      setTimeout(async () => {
        try {
          const row = await refetchRow(o);
          if (wants && row && !offerState(row).visible) {
            showToast(`«${o.name}» на Flowwow пока не видна: ${whyHidden(row)}`, 'error');
          }
        } catch { /* покажет следующее обновление */ }
        setPendingFw(prev => { const n = { ...prev }; delete n[key]; return n; });
      }, 4000);
    } catch (error) {
      setPendingFw(prev => { const n = { ...prev }; delete n[key]; return n; });
      showToast(error.message || 'Не удалось переключить', 'error');
    } finally {
      setBusy(null);
    }
  };

  // 📌 «Топ»: закрепить позицию в топ-18 магазина или открепить. Сам топ уходит на площадку
  // ближайшей синхронизацией (пара секунд) — тогда и перечитываем строку.
  const [pendingTop, setPendingTop] = useState({});
  const topWaitReason = (o) => (!offerState(o).visible ? whyHidden(o)
    : o.remote_active !== true ? 'площадка ещё не подтвердила, что карточка активна'
      : 'попадёт при ближайшей синхронизации');
  const toggleTop = async (o) => {
    const key = rowKey(o);
    const wants = !(pendingTop[key] ?? o.top_pinned);
    setPendingTop(prev => ({ ...prev, [key]: wants }));
    setBusy(`top-${key}`);
    try {
      await api.setFlowwowTopPinned(o.kind, o.entity_id, wants);
      showToast(wants ? `Закрепили в топе: «${o.name}»` : `Открепили от топа: «${o.name}»`);
      setTimeout(async () => {
        try { await refetchRow(o); } catch { /* покажет следующее обновление */ }
        setPendingTop(prev => { const n = { ...prev }; delete n[key]; return n; });
      }, 4000);
    } catch (error) {
      setPendingTop(prev => { const n = { ...prev }; delete n[key]; return n; });
      showToast(error.message || 'Не удалось изменить топ', 'error');
    } finally {
      setBusy(null);
    }
  };

  // Переключатель «У нас» — тот же «Виден», что в каталоге: меняет витрину везде,
  // Flowwow следует за ней. Сервер не покажет стебель без остатка и плановой поставки.
  const toggleHere = async (o) => {
    const wants = !o.shown_here;
    if (wants && !o.has_category) {
      showToast('Нет категории — на витрине не показывается. Укажите категорию в карточке', 'error');
      return;
    }
    const key = rowKey(o);
    setBusy(`here-${key}`);
    try {
      const res = o.kind === 'product'
        ? await api.updateProduct(o.entity_id, { is_hidden: !wants })
        : await api.updateBouquet(o.entity_id, { is_hidden: !wants });
      const stillHidden = wants && res && res.is_hidden === true;
      setOffers(prev => prev && ({
        ...prev,
        items: prev.items.map(x => (x.kind === o.kind && x.entity_id === o.entity_id) ? { ...x, shown_here: wants && !stillHidden } : x),
      }));
      if (stillHidden) showToast(`«${o.name}» не показать: остаток меньше шага продажи и плановой поставки нет`, 'error');
      setTimeout(() => { refetchRow(o).catch(() => {}); }, 4000);
    } catch (error) {
      showToast(error.message || 'Не удалось переключить', 'error');
    } finally {
      setBusy(null);
    }
  };

  const loadPreview = useCallback(async () => {
    setTabLoading(true);
    try { setPreview(await api.getFlowwowPreview()); }
    catch (error) { showToast(error.message || 'Ошибка предпросмотра', 'error'); }
    finally { setTabLoading(false); }
  }, [showToast]);

  const loadLog = useCallback(async (kind = logKind) => {
    setTabLoading(true);
    try { setLog(await api.getFlowwowLog(200, kind || null)); }
    catch (error) { showToast(error.message || 'Ошибка загрузки журнала', 'error'); }
    finally { setTabLoading(false); }
  }, [showToast, logKind]);

  const loadForeign = useCallback(async () => {
    setTabLoading(true);
    try {
      const data = await api.getFlowwowForeign();
      setForeign(data);
      setForeignSelected(new Set());
      // Подсказанные пары — сразу в выбор; админ поправит или снимет
      const links = {};
      for (const i of data.items || []) if (i.suggested_bouquet_id) links[i.product_id] = String(i.suggested_bouquet_id);
      setForeignLinks(links);
    }
    catch (error) { showToast(error.message || 'Ошибка загрузки чужих карточек', 'error'); }
    finally { setTabLoading(false); }
  }, [showToast]);

  const linkForeign = async () => {
    const links = Array.from(foreignSelected)
      .filter(pid => foreignLinks[pid])
      .map(pid => ({ product_id: pid, bouquet_id: parseInt(foreignLinks[pid], 10) }));
    if (links.length === 0) { showToast('Выберите карточки и букеты для них', 'error'); return; }
    if (!(await nfConfirm(`Привязать ${links.length} карточек к нашим букетам? Карточки останутся на Flowwow с отзывами и историей продаж. При следующей синхронизации в них уйдут наши фото, состав, цена и размеры; название останется как на Flowwow (станет «Названием для Flowwow» букета). Если у нашего букета нет описания — возьмём его с карточки. Фото — только наши: у букетов без фото карточка будет скрыта, пока не добавите фото в наш каталог. Площадка может заново проверить карточку.`, { confirmText: 'Привязать' }))) return;
    setBusy('link');
    try {
      const res = await api.linkFlowwowForeign(links);
      const errs = res.errors || [];
      const took = [res.descriptions ? `описаний с карточек: ${res.descriptions}` : '', res.no_photo ? `без фото: ${res.no_photo} — карточки скрыты, пока не добавите фото` : ''].filter(Boolean).join(', ');
      showToast(`Привязано: ${res.linked} из ${res.requested}${took ? ` · ${took}` : ''}${errs.length ? ` · не привязано ${errs.length}: ${errs.slice(0, 2).map(e => e.message).join('; ')}` : ''}`, errs.length ? 'error' : 'success');
      setOffers(null);
      loadForeign();
    } catch (error) {
      showToast(error.message || 'Ошибка привязки', 'error');
    } finally {
      setBusy(null);
    }
  };

  const archiveForeign = async () => {
    // В архив — только отмеченные без выбранного букета: карточки с парой уходят в «Привязать»
    const ids = Array.from(foreignSelected).filter(pid => !foreignLinks[pid]);
    if (ids.length === 0) return;
    if (!(await nfConfirm(`Увести в архив ${ids.length} карточек на Flowwow — отмеченные, у которых не выбран наш букет? Их отзывы и история уйдут вместе с ними; вернуть можно только из приложения Flowwow Seller.`, { confirmText: 'В архив' }))) return;
    setBusy('archive');
    try {
      const res = await api.archiveFlowwowForeign(ids);
      showToast(`В архив: ${res.archived} из ${res.matched}`);
      loadForeign();
    } catch (error) {
      showToast(error.message || 'Ошибка архивации', 'error');
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'offers' && offers === null) loadOffers();
    if (activeTab === 'preview' && preview === null) loadPreview();
    if (activeTab === 'log' && log === null) loadLog();
    if (activeTab === 'foreign' && foreign === null) loadForeign();
  }, [activeTab, offers, preview, log, foreign, loadOffers, loadPreview, loadLog, loadForeign]);


  const saveField = async (key, value) => {
    const norm = SETTING_API[key];
    if (!norm) return;
    const v = norm(value);
    if (savedRef.current && norm(savedRef.current[key]) === v) return;
    const payload = { [`flowwow_${key}`]: v };
    // самодоставка — только вместе с курьером Flowwow (без него тоггл скрыт)
    if (key === 'delivery_courier' && !v) payload.flowwow_self_delivery = false;
    try {
      const saved = await api.updateFlowwowSettings(payload);
      savedRef.current = saved;
      setSettings(prev => ({ ...prev, ...saved }));
      setPreview(null); // предпросмотр зависит от комиссии/габаритов — перечитаем при открытии
      setOffers(null);
      showToast('Сохранено');
    } catch (error) {
      showToast(error.message || 'Не сохранилось', 'error');
      if (savedRef.current) setSettings(prev => ({ ...prev, [key]: savedRef.current[key] }));
    }
  };
  // Переключатель: сразу в состояние и на сервер
  const toggleAndSave = (key) => async (v) => {
    if (key === 'sync_enabled' && !(await nfConfirm(v
      ? 'Включить обмен с Flowwow? Карточки, остатки и цены начнут уходить на площадку, заказы — приходить и приниматься автоматически.'
      : 'Выключить обмен с Flowwow? Карточки перестанут обновляться, новые заказы — приходить сюда. Только на время работ.',
    { confirmText: v ? 'Включить' : 'Выключить' }))) return;
    set(key)(v);
    saveField(key, v);
  };
  // Поле ввода: сохраняем, когда вышли из него
  const saveOnBlur = (key) => () => saveField(key, settings?.[key]);
  // Ползунок топ-18: сохраняем через долю секунды после последнего движения
  const top18Timer = useRef(null);
  const slideTop18 = (v) => {
    set('top18_bouquets')(v);
    clearTimeout(top18Timer.current);
    top18Timer.current = setTimeout(() => saveField('top18_bouquets', v), 600);
  };

  const togglePause = async () => {
    const next = !status?.paused;
    if (next && !(await nfConfirm('Поставить продажи на Flowwow на паузу? Все карточки скроются с витрины площадки, магазин там погаснет. Уже принятые заказы нужно выполнить — они продолжат приходить сюда.', { confirmText: 'На паузу' }))) return;
    setBusy('pause');
    try {
      const res = await api.setFlowwowPaused(next);
      setStatus(prev => ({ ...prev, paused: res.paused }));
      setOffers(null);
      showToast(res.paused ? 'Пауза включена: карточки скроются через несколько секунд' : 'Пауза снята: карточки вернутся на витрину через несколько секунд');
    } catch (error) {
      showToast(error.message || 'Ошибка', 'error');
    } finally {
      setBusy(null);
    }
  };

  const runAction = async (kind) => {
    setBusy(kind);
    try {
      let data;
      let title;
      if (kind === 'poll') { data = await api.pollFlowwowOrders(); title = 'Проверка заказов'; }
      else if (kind === 'remote') { data = await api.refreshFlowwowRemote(); title = 'Состояние карточек на Flowwow'; }
      else if (kind === 'webhook') { data = await api.testFlowwowWebhook(); title = 'Проверка вебхука'; }
      else { data = await api.runFlowwowSync(kind === 'full'); title = kind === 'full' ? 'Полная сверка' : 'Синхронизация'; }
      setActionResult({ title, data, at: new Date() });
      showToast(`${title}: готово`);
      // Результат меняет привязки, журнал и счётчики — перечитаем
      setOffers(null); setLog(null); setPreview(null);
      loadStatus(true);
    } catch (error) {
      showToast(error.message || 'Ошибка', 'error');
      setActionResult({ title: 'Ошибка', data: { error: error.message }, at: new Date() });
    } finally {
      setBusy(null);
    }
  };

  const copyWebhook = async () => {
    const url = status?.webhook_url || '';
    try {
      await navigator.clipboard.writeText(url);
      showToast('Адрес вебхука скопирован');
    } catch (e) {
      window.prompt('Скопируйте адрес вебхука', url);
    }
  };

  const set = (key) => (v) => setSettings(prev => ({ ...prev, [key]: v }));

  const deliveryOwn = !!settings?.delivery_own;
  useEffect(() => {
    if (!deliveryOwn || tariff) return;
    api.getDeliveryTariffIntervals().then(t => { setTariff(t); setTariffError(false); }).catch(() => setTariffError(true));
  }, [deliveryOwn, tariff]);

  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const paused = !!status?.paused;
  const shop = status?.shop;
  const counts = status?.offers || {};
  const lastSync = status?.last_sync || {};
  const plan = preview?.plan || {};
  const syncOff = settings && settings.sync_enabled === false;

  // Платная опция: включается разработчиком платформы через .env (FLOWWOW_TOKEN + FLOWWOW_SHOP_ID).
  // Без подключения — только что это и к кому обратиться, без технических инструкций и настроек.
  if (!enabled) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Flowwow</h1>
        </div>
        <div className="card" style={{ maxWidth: 720 }}>
          <div className="card-title">Продажи на Flowwow</div>
          <p style={{ margin: '0 0 10px', fontSize: 14, lineHeight: 1.5 }}>
            Ваша витрина на маркетплейсе Flowwow без ручной работы: стебли поштучно и букеты публикуются
            и обновляются автоматически, остатки и цены (с учётом комиссии площадки) синхронизируются при каждом
            изменении, заказы с Flowwow попадают в систему как обычные — с резервом на складе, фото до
            доставки и передачей курьеру из админки.
          </p>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: '#5E6561' }}>
            Интеграция подключается отдельно. Чтобы включить её для вашего магазина, обратитесь к
            разработчику платформы.
          </p>
        </div>
      </div>
    );
  }

  // Одна кнопка на странице: состояние интеграции + данные открытой вкладки
  const refreshAll = () => {
    loadStatus();
    if (activeTab === 'offers') loadOffers();
    else if (activeTab === 'preview') loadPreview();
    else if (activeTab === 'foreign') loadForeign();
    else if (activeTab === 'log') loadLog();
  };

  const stateLabel = paused ? 'Пауза продаж' : status?.ping_error ? 'Ошибка связи' : syncOff ? 'Остановлена' : 'Работает';
  const stateCls = paused || syncOff ? 'pending' : status?.ping_error ? 'cancelled' : 'completed';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Flowwow</h1>
        <button type="button" className="btn btn-secondary btn-md" onClick={refreshAll} disabled={!!busy || tabLoading}
          title="Перечитать состояние и открытую вкладку (с Flowwow не синхронизирует)">
          Обновить
        </button>
      </div>

      {/* Статус интеграции */}
      <div className="card">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          Продажи на Flowwow
          <span className={`badge badge-${stateCls}`}>{stateLabel}</span>
          {status?.pending_accept > 0 && (
            <span className="badge badge-cancelled" title="Заказы, которые площадка ещё не считает принятыми — у магазина 45 минут">Ждут принятия: {status.pending_accept}</span>
          )}
          {status?.refuse_problems > 0 && (
            <span className="badge badge-cancelled" title="Отменённые у нас заказы, отказ по которым не дошёл до площадки — откройте карточку заказа">Отказ не дошёл: {status.refuse_problems}</span>
          )}
        </div>
        {status?.read_only && (
          <div style={{ margin: '-4px 0 14px', padding: '8px 12px', background: '#F9EBCF', color: '#7E5D00', '--ch': '6px', fontSize: 13, fontWeight: 600 }}>
            Только чтение: карточки и заказы с Flowwow видны, но на площадку отсюда ничего не уходит — ни карточки, ни цены, ни привязки, ни статусы заказов
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, fontSize: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4 }}>Магазин</div>
            <div>
              <div style={{ fontWeight: 600 }}>{shop?.name || '—'} <span style={{ color: '#5E6561', fontWeight: 400 }}>· #{status?.shop_id}</span></div>
              {shop?.status && <div style={{ fontSize: 12, color: '#5E6561' }}>Статус на Flowwow: {shop.status === 'active' ? 'активен' : shop.status === 'moderation' ? 'на модерации' : shop.status === 'disabled' ? 'выключен' : shop.status}</div>}
              {shop?.address && <div style={{ fontSize: 12, color: '#5E6561' }}>{shop.address}</div>}
            </div>
            {status?.ping_error && (
              <div style={{ marginTop: 6, padding: '6px 10px', background: '#FFE6E3', '--ch': '5px', color: '#CB4D45', fontSize: 12 }}>
                Не удалось связаться с Flowwow: {status.ping_error}. Если не проходит больше часа — сообщите разработчику платформы.
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4 }}>Пауза продаж</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button className={`btn btn-sm ${paused ? 'btn-primary' : 'btn-danger-outline'}`} disabled={!!busy} onClick={togglePause}>
                {busy === 'pause' ? 'Сохраняем...' : paused ? 'Снять паузу — вернуть карточки' : 'Поставить на паузу'}
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#5E6561', marginTop: 4, lineHeight: 1.4 }}>
              {paused
                ? 'Карточки скрыты с витрины Flowwow, новые заказы не приходят. Уже принятые заказы нужно выполнить — они продолжают обновляться.'
                : 'Праздник, перегруз, нет цветов: пауза скрывает все карточки на Flowwow, магазин там гаснет. Заказы, принятые до паузы, остаются в работе.'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4 }}>Последнее обновление витрины</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{formatDateTime(lastSync.at)}</span>
              {lastSync.status && statusBadge(lastSync.status)}
            </div>
            {lastSync.status === 'error' && !advanced && (
              <div style={{ fontSize: 12, color: '#CB4D45', marginTop: 4 }}>
                Часть карточек не обновилась — откройте «{catalogLabels.products}» или «{catalogLabels.bouquets}», колонка «Почему не видна».
              </div>
            )}
            {advanced && lastSync.message && <div style={{ fontSize: 12, color: '#5E6561', marginTop: 4, wordBreak: 'break-word' }}>{lastSync.message}</div>}
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4 }}>Карточки на Flowwow</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span className="badge badge-completed">на витрине {Math.max(0, (counts.published ?? 0) - (counts.hidden ?? 0))}</span>
              <span className="badge badge-pending">скрыто {counts.hidden ?? 0}</span>
              {counts.errors > 0 && <span className="badge badge-cancelled">с ошибкой {counts.errors}</span>}
            </div>
          </div>

          {advanced && (
          <div>
            <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4 }}>Вебхук</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <code style={{ fontSize: 12, background: '#F4F4EF', padding: '3px 6px', '--ch': '4px', wordBreak: 'break-all' }}>{status?.webhook_url}</code>
              <button type="button" className="btn btn-ghost btn-sm" onClick={copyWebhook}>Копировать</button>
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: status?.webhook_secret_set ? '#1C3D2E' : '#B0603C' }}>
              {status?.webhook_secret_set ? 'Секрет задан (FLOWWOW_WEBHOOK_SECRET)' : 'Секрет не задан — вебхук отвечает 401, работает только опрос'}
            </div>
          </div>
          )}
        </div>

        {advanced && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => runAction('sync')}>
            {busy === 'sync' ? 'Синхронизация...' : 'Синхронизировать'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => runAction('full')}>
            {busy === 'full' ? 'Сверка...' : 'Полная сверка'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => runAction('poll')}>
            {busy === 'poll' ? 'Проверка...' : 'Проверить заказы'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => runAction('remote')}
            title="Снять с площадки: активна ли карточка и сколько фото подгрузилось. Flowwow активирует карточку только после загрузки хотя бы одного фото.">
            {busy === 'remote' ? 'Проверка...' : 'Проверить на Flowwow'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => runAction('webhook')}
            title="Flowwow пошлёт тестовое событие на адрес вебхука, который у него настроен. В ответе — код, который получил их сервер, и дошёл ли хук сюда.">
            {busy === 'webhook' ? 'Проверка...' : 'Проверить вебхук'}
          </button>
        </div>
        )}

        {actionResult && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {actionResult.title} <span style={{ color: '#5E6561', fontWeight: 400 }}>· {formatDateTime(actionResult.at.toISOString())}</span>
              </div>
              <button type="button" className="modal-close" onClick={() => setActionResult(null)} title="Закрыть" aria-label="Закрыть">&times;</button>
            </div>
            <pre style={{ margin: 0, padding: 12, background: '#F4F4EF', '--ch': '6px', fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(actionResult.data, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Настройки</button>
        <button className={`tab ${activeTab === 'offers' && offerKind === 'product' ? 'active' : ''}`} onClick={() => { setActiveTab('offers'); setOfferKind('product'); }}>{catalogLabels.products}</button>
        <button className={`tab ${activeTab === 'offers' && offerKind === 'bouquet' ? 'active' : ''}`} onClick={() => { setActiveTab('offers'); setOfferKind('bouquet'); }}>{catalogLabels.bouquets}</button>
        {advanced && <button className={`tab ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveTab('preview')}>Предпросмотр</button>}
        {advanced && <button className={`tab ${activeTab === 'log' ? 'active' : ''}`} onClick={() => setActiveTab('log')}>Журнал</button>}
        {advanced && <button className={`tab ${activeTab === 'foreign' ? 'active' : ''}`} onClick={() => setActiveTab('foreign')}>Чужие карточки</button>}
      </div>

      {activeTab === 'settings' && settings && (
        <div className="card">
          <div className="card-title">Что и как публикуем</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
            <Toggle
              checked={!!settings.publish_stems}
              onChange={toggleAndSave('publish_stems')}
              label="Публиковать стебли"
              hint="Товары каталога уходят на Flowwow поштучно, в раздел «Цветы поштучно»"
            />
            <Toggle
              checked={!!settings.publish_bouquets}
              onChange={toggleAndSave('publish_bouquets')}
              label="Публиковать букеты"
              hint="Собранные партии — «в наличии», рецепты — «под заказ» со сроком сборки"
            />
            {settings.publish_bouquets && (
            <Toggle
              checked={!!settings.recipe_bouquets_stock}
              onChange={toggleAndSave('recipe_bouquets_stock')}
              label="Букеты по рецепту — с количеством"
              hint="Вкл — «в наличии N»: сколько можно собрать из склада сейчас, больше Flowwow заказать не даст; срока сборки нет — покупатель может выбрать ближайшее время. Выкл — «под заказ» со сроком сборки, количество не ограничено"
            />
            )}
            {advanced && (
            <Toggle
              checked={!!settings.sync_enabled}
              onChange={toggleAndSave('sync_enabled')}
              label="Служебно: обмен с Flowwow включён"
              hint="Выключить только на время работ разработчика: останавливает и витрину, и приём заказов. Для праздников — пауза продаж выше"
            />
            )}
            {settings.orders_since > 0 && (
              <small style={{ display: 'block', color: '#5E6561', fontSize: 12 }}>
                Заказы с Flowwow принимаем, начиная с {new Date(settings.orders_since * 1000).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — более ранние магазин ведёт в приложении Flowwow Seller
              </small>
            )}
            {advanced && (
            <Toggle
              checked={!!settings.auto_accept}
              onChange={toggleAndSave('auto_accept')}
              label="Автопринятие заказов"
              hint="Новый заказ принимается сразу; иначе у админа 45 минут на кнопку «Принять на Flowwow». Отказы штрафуются — выключать не стоит"
            />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '12px 16px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Комиссия Flowwow, %</label>
              <NumInput
                min={0} max={90} fallback={0}
                className="form-input"
                value={settings.markup_percent}
                onChange={set('markup_percent')}
                onBlur={saveOnBlur('markup_percent')}
              />
              <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Ставка, которую Flowwow удерживает с вас (смотрите в кабинете Flowwow). Цена на площадке считается так, чтобы после удержания вам осталась цена витрины (с сервисным сбором): 1 000 ₽ → 1 538 ₽ при 35 %. 0 — цена как на витрине</small>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Комиссия на стебли, %</label>
              <input
                type="text" inputMode="decimal"
                className="form-input"
                placeholder={`как общая — ${settings.markup_percent ?? 0}`}
                value={settings.markup_percent_stems ?? ''}
                onChange={(e) => { if (/^\d*([.,]\d*)?$/.test(e.target.value)) set('markup_percent_stems')(e.target.value); }}
                onBlur={saveOnBlur('markup_percent_stems')}
              />
              <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Если со стеблей Flowwow удерживает другую ставку, чем с букетов. Пусто — та же, что выше. Фактическая ставка по заказам видна в карточке заказа и в аналитике</small>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Запас дней</label>
              <NumInput
                integer min={0} max={30} fallback={2}
                className="form-input"
                value={settings.min_days_left}
                onChange={set('min_days_left')}
                onBlur={saveOnBlur('min_days_left')}
              />
              <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Не показывать на Flowwow цветы, срок продажи которых кончается раньше, чем через N дней. Если следом приходит плановая накладная — показываем меньшее из остатка и свободного в плановой</small>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Время сборки букета, ч</label>
              <NumInput
                integer min={1} max={72} fallback={2}
                className="form-input"
                value={settings.bouquet_production_hours}
                onChange={set('bouquet_production_hours')}
                onBlur={saveOnBlur('bouquet_production_hours')}
              />
              <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Срок «под заказ» для букетов без собранной партии, которые собираем по рецепту из того, что на складе{settings.recipe_bouquets_stock ? '. Сейчас не используется: букеты по рецепту уходят с количеством' : ''}</small>
            </div>
            {advanced && (<>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Плановая на сегодня, ч</label>
              <NumInput
                integer min={1} max={72} fallback={4}
                className="form-input"
                value={settings.planned_today_lead_hours}
                onChange={set('planned_today_lead_hours')}
                onBlur={saveOnBlur('planned_today_lead_hours')}
              />
              <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Срок «под заказ», когда позиция ждёт плановую накладную с датой прихода сегодня</small>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Высота стебля для предзаполнения, см</label>
              <NumInput
                integer min={1} max={9999} fallback={50}
                className="form-input"
                value={settings.default_stem_height_cm}
                onChange={set('default_stem_height_cm')}
                onBlur={saveOnBlur('default_stem_height_cm')}
              />
              <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Подставляется товарам без высоты при подключении; дальше поле обязательно в карточке товара</small>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Ширина букета для предзаполнения, см</label>
              <NumInput
                integer min={1} max={9999} fallback={40}
                className="form-input"
                value={settings.default_bouquet_width_cm}
                onChange={set('default_bouquet_width_cm')}
                onBlur={saveOnBlur('default_bouquet_width_cm')}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Высота букета для предзаполнения, см</label>
              <NumInput
                integer min={1} max={9999} fallback={50}
                className="form-input"
                value={settings.default_bouquet_height_cm}
                onChange={set('default_bouquet_height_cm')}
                onBlur={saveOnBlur('default_bouquet_height_cm')}
              />
            </div>
            </>)}
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #EBEDEC', maxWidth: 560 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Топ-18 магазина</div>
            <small style={{ display: 'block', color: '#999', fontSize: 12, marginBottom: 12 }}>
              Первый экран вашего магазина на Flowwow. Закреплённые (📌 во вкладках «{catalogLabels.products}» и «{catalogLabels.bouquets}») — всегда в топе, свободные места заполняются сами
            </small>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#5E6561' }}>{catalogLabels.products}</span>
              <input
                type="range" min={0} max={18} step={1}
                value={settings.top18_bouquets ?? 9}
                onChange={(e) => slideTop18(parseInt(e.target.value, 10))}
                style={{ flex: 1, accentColor: '#1C3D2E' }}
              />
              <span style={{ fontSize: 13, color: '#5E6561' }}>{catalogLabels.bouquets}</span>
            </div>
            <div style={{ fontSize: 13, marginTop: 6 }}>
              Букетов: <b>{settings.top18_bouquets ?? 9}</b> · стеблей: <b>{18 - (settings.top18_bouquets ?? 9)}</b>
              <span style={{ color: '#999' }}> — вместе с закреплёнными; не хватает одних — добираем другими</span>
            </div>
            <div style={{ marginTop: 12 }}>
              <Toggle
                checked={!!settings.top18_auto}
                onChange={toggleAndSave('top18_auto')}
                label="Свободные места заполнять автоматически"
                hint="По количеству в наличии: букеты, которых больше всего готово к сборке, и стебли с наибольшим остатком. Выключите — в топе будут только закреплённые"
              />
            </div>
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #EBEDEC', maxWidth: 640 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Способы доставки</div>
            <small style={{ display: 'block', color: '#999', fontSize: 12, marginBottom: 12 }}>
              Включаются в кабинете Flowwow, здесь — что мы делаем с заказом. Пришёл заказ способом, который здесь выключен, — в заказе будет пометка
            </small>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Toggle
                checked={!!settings.delivery_own}
                onChange={toggleAndSave('delivery_own')}
                label="Своя доставка по зонам"
                hint="Везёт ваш курьер. Доставка в заказе — по вашему тарифу, как на витрине, курьеру — его доля. «У курьера» и «Завершён» сами уходят на Flowwow"
              />
              <Toggle
                checked={!!settings.delivery_courier}
                onChange={toggleAndSave('delivery_courier')}
                label="Курьер Flowwow"
                hint="Курьер площадки забирает заказ из магазина и закрывает его сам; доставку покупатель платит Flowwow"
              />
              {settings.delivery_courier && (
                <Toggle
                  checked={!!settings.self_delivery}
                  onChange={toggleAndSave('self_delivery')}
                  label="Берём самодоставку"
                  hint="Flowwow не нашёл курьера и предложил везти самим: примите в приложении Flowwow Seller и нажмите «Везём сами» в заказе — он станет вашей доставкой"
                />
              )}
              <Toggle
                checked={!!settings.delivery_pickup}
                onChange={toggleAndSave('delivery_pickup')}
                label="Самовывоз"
                hint="Покупатель забирает сам: код получения — в приложении Flowwow Seller, сверьте его с покупателем и нажмите «Завершён»"
              />
            </div>

            {/* Flowwow не присылает населённый пункт — адрес подбираем в этом радиусе от склада (владелец 25.10) */}
            {(settings.delivery_own || (settings.delivery_courier && settings.self_delivery)) && (
              <div className="form-group" style={{ margin: '14px 0 0', maxWidth: 320 }}>
                <label className="form-label" htmlFor="fw-address-radius">Зона своей доставки на Flowwow, км</label>
                <input
                  id="fw-address-radius" type="text" inputMode="numeric"
                  className="form-input"
                  placeholder="80"
                  value={settings.address_radius_km ?? ''}
                  onChange={(e) => { if (/^\d{0,3}$/.test(e.target.value)) set('address_radius_km')(e.target.value); }}
                  onBlur={saveOnBlur('address_radius_km')}
                />
                <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>
                  Как в Flowwow Seller. Адрес без города ищем в этом радиусе от склада: дальше не подставляем — заказ придёт с пометкой, адрес выберете в карточке. Пусто — 80 км
                </small>
              </div>
            )}

            {settings.delivery_own && tariffError && !tariff && (
              <small style={{ display: 'block', color: '#CB4D45', fontSize: 12, marginTop: 12 }}>
                Тариф доставки не загрузился — подсказка зон появится после обновления страницы
              </small>
            )}
            {settings.delivery_own && tariff?.intervals?.length > 0 && (() => {
              const { zones, more } = flowwowZones(tariff.intervals);
              const cell = { padding: '6px 10px', borderBottom: '1px solid #EBEDEC', fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' };
              const head = { ...cell, fontSize: 11, fontWeight: 700, color: '#5E6561', textTransform: 'uppercase', letterSpacing: '0.04em' };
              return (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Зоны для кабинета Flowwow</div>
                  <small style={{ display: 'block', color: '#999', fontSize: 12, marginBottom: 8 }}>
                    Из вашего тарифа. Зона Flowwow — радиус от магазина по прямой, ваш тариф — по дорогам, поэтому радиус меньше; цена выше на 3 % — столько Flowwow удерживает с доставки. Бесплатную доставку от суммы на Flowwow не включайте: курьеру вы всё равно платите
                  </small>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', minWidth: 420 }}>
                      <thead>
                        <tr>
                          <th style={{ ...head, textAlign: 'left' }}>Зона</th>
                          <th style={head}>Радиус на Flowwow</th>
                          <th style={head}>Цена на Flowwow</th>
                          <th style={head}>Ваш тариф</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zones.map((z, i) => (
                          <tr key={i}>
                            <td style={{ ...cell, textAlign: 'left', color: '#5E6561' }}>{i + 1}</td>
                            <td style={{ ...cell, fontWeight: 600 }}>до {fmtKm(z.radius)} км</td>
                            <td style={{ ...cell, fontWeight: 600, color: '#1C3D2E' }}>{z.fwPrice.toLocaleString('ru-RU')} ₽</td>
                            <td style={{ ...cell, color: '#5E6561' }}>до {fmtKm(z.ourKm)} км — {Number(z.ourPrice).toLocaleString('ru-RU')} ₽</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {more && (
                    <small style={{ display: 'block', color: '#999', fontSize: 12, marginTop: 6 }}>
                      На Flowwow не больше {FW_ZONES_MAX} зон: дальше {fmtKm(zones[zones.length - 1].radius)} км доставку там не предлагаем
                    </small>
                  )}
                </div>
              );
            })()}
          </div>

          {advanced && (
          <div className="form-group" style={{ marginTop: 16, marginBottom: 0 }}>
            <label className="form-label">Картинка открытки (URL)</label>
            <input
              type="text"
              className="form-input"
              placeholder="https://…/postcard.jpg"
              value={settings.postcard_image_url || ''}
              onChange={(e) => set('postcard_image_url')(e.target.value)}
              onBlur={saveOnBlur('postcard_image_url')}
            />
            <small style={{ display: 'block', marginTop: 4, color: '#999', fontSize: 12 }}>Открытка публикуется отдельной карточкой; пусто — открытку на Flowwow не выкладываем</small>
          </div>
          )}

          <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'right' }}>
            Изменения сохраняются сами: переключатели — сразу, поля — когда выйдете из них
          </div>
        </div>
      )}

      {activeTab === 'offers' && (
        <>
          <div style={{ fontSize: 13, color: '#5E6561', maxWidth: 820, marginBottom: 12 }}>
            «У нас» — тот же переключатель «Виден», что в каталоге. «На Flowwow» — видна ли карточка покупателям Flowwow сейчас;
            выключите — позиция останется только у вас. Видна у нас, но не на Flowwow — рядом причина.
            «Топ» — первый экран магазина на Flowwow: 📌 закрепляет позицию, «авто» — попала сама, по количеству.
          </div>
          {offers?.top18 && (() => {
            const t = offers.top18;
            const inTop = (offers.items || []).filter(o => o.in_top);
            const pinnedIn = inTop.filter(o => o.top_pinned).length;
            const bq = inTop.filter(o => o.kind === 'bouquet').length;
            return (
              <div style={{ fontSize: 13, marginBottom: 12, padding: '8px 12px', background: '#F4F4EF', '--ch': '6px', maxWidth: 820 }}>
                <b>Топ-18:</b> {inTop.length} из {t.size} — закреплено {pinnedIn}, авто {inTop.length - pinnedIn}
                {' '}· букетов {bq}, стеблей {inTop.length - bq}
                {t.pinned > pinnedIn && <> · ещё закреплено и ждёт: {t.pinned - pinnedIn}</>}
                {' · '}
                {!t.sent_ok
                  ? <span style={{ color: '#CB4D45' }}>площадка не приняла список: {t.error || 'ошибка'}</span>
                  : t.sent_at
                    ? <span style={{ color: '#5E6561' }}>отправлен на Flowwow {formatDateTime(t.sent_at)}</span>
                    : <span style={{ color: '#5E6561' }}>ещё не отправлялся</span>}
              </div>
            );
          })()}
          {tabLoading && !offers ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <div className="table-container">
              <table className="table" style={{ minWidth: (advanced ? 1160 : 860) + (offerKind === 'bouquet' ? 180 : 0) }}>
                <thead>
                  <tr>
                    <th style={{ userSelect: 'none', position: 'relative', minWidth: 240 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (offerSearchOpen || offerQuery) ? 'hidden' : 'visible' }}>
                        <span style={{ cursor: 'pointer' }} onClick={() => sortBy('name')}>
                          Название<SortIcon active={offerSort.field === 'name'} dir={offerSort.dir} />
                        </span>
                        <button onClick={() => setOfferSearchOpen(true)} title="Поиск по названию"
                          style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
                            color: '#1C3D2E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', '--ch': '5px' }}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                        </button>
                      </div>
                      {(offerSearchOpen || offerQuery) && (
                        <div style={{ position: 'absolute', left: 12, right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1C3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}>
                            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                          <input autoFocus type="text" placeholder="Поиск по названию" value={offerQuery}
                            onChange={e => setOfferQuery(e.target.value)}
                            onBlur={() => { if (!offerQuery) setOfferSearchOpen(false); }}
                            style={{ padding: '6px 26px 6px 30px', border: '1px solid #1C3D2E', '--ch': '6px', '--stroke': '#1C3D2E',
                              fontSize: 13, fontWeight: 500, width: '100%', boxSizing: 'border-box', outline: 'none',
                              background: '#fff', color: '#1C3D2E', textTransform: 'none', letterSpacing: '0.01em' }} />
                          {offerQuery && (
                            <button onClick={() => { setOfferQuery(''); setOfferSearchOpen(false); }} title="Очистить"
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18,
                                border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff',
                                fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                          )}
                        </div>
                      )}
                    </th>
                    {offerKind === 'bouquet' && (
                      <th title="Как букет называется на Flowwow: «Название для Flowwow» из карточки букета, если заполнено; серым — уходит наше название">
                        Название на Flowwow
                      </th>
                    )}
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('here')}
                      title="Виден в нашем каталоге — тот же переключатель, что в каталоге">
                      У нас<SortIcon active={offerSort.field === 'here'} dir={offerSort.dir} />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('visible')}
                      title="Видна ли карточка покупателям Flowwow сейчас. Выключите — позиция останется только у вас">
                      На Flowwow<SortIcon active={offerSort.field === 'visible'} dir={offerSort.dir} />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('top')}
                      title="Топ-18 магазина на Flowwow: 📌 — закреплена вами, «авто» — попала сама, по количеству в наличии">
                      Топ<SortIcon active={offerSort.field === 'top'} dir={offerSort.dir} />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('stock')}>
                      Остаток<SortIcon active={offerSort.field === 'stock'} dir={offerSort.dir} />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('price')}>
                      Цена там<SortIcon active={offerSort.field === 'price'} dir={offerSort.dir} />
                    </th>
                    <th>Почему не на Flowwow / ошибка</th>
                    {advanced && <th>offer_id</th>}
                    {advanced && <th title="Снимок с площадки (кнопка «Проверить на Flowwow»): активна ли карточка и сколько фото подгрузилось">Снимок</th>}
                    {advanced && <th>Отправлено</th>}
                  </tr>
                </thead>
                <tbody>
                  {(!offers || offers.items.length === 0) && (
                    <tr><td colSpan={(advanced ? 10 : 7) + (offerKind === 'bouquet' ? 1 : 0)} style={{ textAlign: 'center', padding: '40px 20px', color: '#5E6561' }}>
                      {paused ? 'Продажи на паузе — карточки скрыты' : 'Пока ничего не опубликовано. Карточки появятся сами в течение нескольких минут после подключения'}
                    </td></tr>
                  )}
                  {(() => {
                    // Вкладка «Стебли» — стебли; «Букеты» — букеты и открытка (доп. товар)
                    const all = (offers?.items || []).filter(o => offerKind === 'product' ? o.kind === 'product' : o.kind !== 'product');
                    const q = normName(offerQuery.trim());
                    // Ищем и по нашему названию, и по «Названию для Flowwow» (у букетов)
                    const rows = sortOffers(q ? all.filter(o => normName(o.name).includes(q) || normName(o.flowwow_name).includes(q)) : all, offerSort.field, offerSort.dir);
                    if (all.length === 0 && offers?.items.length > 0) {
                      return (
                        <tr><td colSpan={(advanced ? 10 : 7) + (offerKind === 'bouquet' ? 1 : 0)} style={{ textAlign: 'center', padding: '28px 20px', color: '#5E6561' }}>
                          В этом разделе пока нет позиций
                        </td></tr>
                      );
                    }
                    if (all.length > 0 && rows.length === 0) {
                      return (
                        <tr><td colSpan={(advanced ? 10 : 7) + (offerKind === 'bouquet' ? 1 : 0)} style={{ textAlign: 'center', padding: '28px 20px', color: '#5E6561' }}>
                          Ничего не найдено по «{offerQuery.trim()}»
                        </td></tr>
                      );
                    }
                    return (
                      <>
                        {rows.map(o => {
                        const st = offerState(o);
                        const ours = o.kind === 'product' || o.kind === 'bouquet';
                        const fwOn = pendingFw[rowKey(o)] ?? st.visible;
                        return (
                        <tr key={o.id ?? `${o.kind}-${o.entity_id}`} style={o.last_error ? { background: '#FFF4F0' } : undefined}>
                          <td style={{ fontWeight: 500 }}>{o.name}</td>
                          {offerKind === 'bouquet' && (
                            <td>{o.flowwow_name || <span style={{ color: '#909894' }}>{o.name}</span>}</td>
                          )}
                          <td>
                            {ours ? (
                              <Toggle
                                checked={!!o.shown_here}
                                disabled={o.archived || busy === `here-${rowKey(o)}`}
                                onChange={() => toggleHere(o)}
                              />
                            ) : <span style={{ color: '#BBB' }}>—</span>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <Toggle
                                checked={fwOn}
                                disabled={!ours || busy === `fw-${rowKey(o)}`}
                                onChange={() => toggleFlowwow(o)}
                              />
                              {o.remote_url && st.visible && <a href={o.remote_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>открыть</a>}
                            </span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {ours ? (() => {
                              const pinned = pendingTop[rowKey(o)] ?? o.top_pinned;
                              const title = pinned
                                ? (o.in_top ? 'Закреплена в топе. Нажмите, чтобы открепить'
                                  : `Закреплена, но сейчас не в топе: ${topWaitReason(o)}. Нажмите, чтобы открепить`)
                                : (o.in_top ? 'В топе автоматически — по количеству в наличии. Нажмите, чтобы закрепить'
                                  : 'Нажмите, чтобы закрепить в топе');
                              return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <button
                                    type="button" title={title} onClick={() => toggleTop(o)}
                                    disabled={busy === `top-${rowKey(o)}` || (o.archived && !pinned)}
                                    style={{
                                      background: 'none', border: 'none', padding: 2, cursor: 'pointer', fontSize: 16, lineHeight: 1,
                                      opacity: pinned ? (o.in_top ? 1 : 0.45) : 0.25, filter: pinned ? 'none' : 'grayscale(1)',
                                    }}
                                  >📌</button>
                                  {!pinned && o.in_top && <span className="badge badge-active" style={{ fontSize: 11 }}>авто</span>}
                                  {pinned && !o.in_top && <span style={{ fontSize: 11, color: '#999' }} title={title}>ждёт</span>}
                                </span>
                              );
                            })() : <span style={{ color: '#BBB' }}>—</span>}
                          </td>
                          <td>{o.last_mode === 'production' ? 'под заказ' : (o.last_stock ?? '—')}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{o.last_price != null ? `${Number(o.last_price).toLocaleString('ru-RU')} ₽` : '—'}</td>
                          <td style={{ maxWidth: 360, fontSize: 12 }}>
                            {o.last_error && (
                              <div style={{ color: '#CB4D45', wordBreak: 'break-word' }}>
                                {o.last_error}
                                <div style={{ color: '#999', fontSize: 11 }}>{formatDateTime(o.last_error_at)}</div>
                              </div>
                            )}
                            {!o.last_error && o.reason && <div style={{ color: '#5E6561' }}>{o.reason}</div>}
                            {!o.last_error && !o.reason && o.remote_active === false && (
                              <div style={{ color: '#5E6561' }}>
                                {o.remote_images > 0
                                  ? (o.moderation_attempts > o.moderation_max
                                    ? `не прошла модерацию после ${o.moderation_max} повторных отправок — причина в кабинете Flowwow`
                                    : o.moderation_attempts > 0
                                      ? `не прошла модерацию — повторная отправка ${o.moderation_attempts} из ${o.moderation_max}`
                                      : 'не активна на площадке: идёт модерация Flowwow; если не пройдёт — отправим повторно сами')
                                  : 'нет фото — Flowwow показывает карточку только с фото'}
                              </div>
                            )}
                            {!o.last_error && !o.reason && o.remote_active !== false && !st.visible && (
                          <div style={{ color: '#5E6561' }}>{o.is_published ? 'скрыта — обновится при следующей синхронизации' : 'ещё не выгружена'}</div>
                        )}
                        {!o.last_error && !o.reason && o.remote_active !== false && st.visible && <span style={{ color: '#BBB' }}>—</span>}
                          </td>
                          {advanced && <td><code style={{ fontSize: 12 }}>{o.offer_id || '—'}</code>{o.flowwow_product_id && <div style={{ fontSize: 11, color: '#999' }}>FW #{o.flowwow_product_id}</div>}</td>}
                          {advanced && (
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                              {o.remote_checked_at == null ? <span style={{ color: '#999' }}>—</span> : (
                                <>
                                  {o.remote_active === true && <span className="badge badge-completed">активна</span>}
                                  {o.remote_active === false && <span className="badge badge-cancelled">не активна</span>}
                                  {o.remote_active == null && <span className="badge badge-inactive">нет на площадке</span>}
                                  {o.remote_images != null && <span style={{ marginLeft: 6, color: o.remote_images === 0 ? '#CB4D45' : '#5E6561' }}>фото: {o.remote_images}</span>}
                                  {o.sub_category_id != null && <div style={{ color: '#999', fontSize: 11 }}>{subcatLabel(o.sub_category_id)}</div>}
                                </>
                              )}
                            </td>
                          )}
                          {advanced && <td style={{ whiteSpace: 'nowrap', color: '#5E6561', fontSize: 12 }}>{formatDateTime(o.last_pushed_at)}</td>}
                        </tr>
                        );
                        })}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'preview' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
              <span style={{ color: '#5E6561' }}>Что уйдёт при синхронизации:</span>
              {Object.keys(PLAN_LABELS).map(k => (
                <span key={k} className={`badge ${plan[k] > 0 ? 'badge-info' : 'badge-pending'}`}>{PLAN_LABELS[k]} {plan[k] ?? 0}</span>
              ))}
              {preview?.excluded && <span className="badge badge-pending">не уходит {preview.excluded.length}</span>}
            </div>
          </div>
          {tabLoading && !preview ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <div className="table-container">
              <table className="table" style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Тип</th>
                    <th>Режим</th>
                    <th>Остаток</th>
                    <th>Цена</th>
                    <th>Подкат.</th>
                    <th>Срок</th>
                    <th>Фото</th>
                    <th>Причины</th>
                  </tr>
                </thead>
                <tbody>
                  {(!preview || preview.cards.length === 0) && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 20px', color: '#5E6561' }}>Публиковать нечего — проверьте настройки и остатки</td></tr>
                  )}
                  {preview?.cards.map(c => (
                    <tr key={c.offer_id}>
                      <td style={{ fontWeight: 500 }}>{c.name}<div style={{ fontSize: 11, color: '#999' }}><code>{c.offer_id}</code></div></td>
                      <td>{KIND_LABELS[c.kind] || c.kind}</td>
                      <td>{MODE_LABELS[c.mode] || c.mode}</td>
                      <td>{c.stock ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{c.price != null ? `${Number(c.price).toLocaleString('ru-RU')} ₽` : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{subcatLabel(c.sub_category_id)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {c.production_time ? `${c.production_time.val} ${MEASURE_LABELS[c.production_time.measure] || ''}` : '—'}
                      </td>
                      <td style={c.images === 0 ? { color: '#CB4D45' } : undefined}>{c.images}</td>
                      <td style={{ fontSize: 12, color: '#5E6561', maxWidth: 320 }}>{(c.reasons || []).join('; ') || '—'}</td>
                    </tr>
                  ))}
                  {preview?.excluded?.map(x => (
                    <tr key={`ex-${x.kind}-${x.entity_id}`} style={{ background: '#F9F9F6', color: '#5E6561' }}>
                      <td>{x.name}</td>
                      <td>{KIND_LABELS[x.kind] || x.kind}</td>
                      <td colSpan={6}>не уходит</td>
                      <td style={{ fontSize: 12, maxWidth: 320 }}>{x.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'foreign' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ color: '#5E6561', fontSize: 13, maxWidth: 720 }}>
              Карточки на площадке, которых интеграция не знает — заведены руками в приложении Flowwow до подключения.
              Букеты лучше привязать к нашим: карточка сохранит отзывы и историю продаж, а фото, состав и цена станут нашими.
              Пара подставлена по названию — проверьте. Что не нужно — в архив.
              <div style={{ marginTop: 4, color: '#B0603C' }}>Привязывайте до включения обмена: включённый обмен сам создаст карточки всем букетам, и привязывать будет не к чему.</div>
              {foreign && <div style={{ marginTop: 4 }}>На площадке всего: <b>{foreign.total_on_shop}</b>, чужих: <b>{foreign.foreign}</b></div>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" disabled={!foreign || foreign.items.length === 0}
                onClick={() => setForeignSelected(foreignSelected.size === (foreign?.items.length || 0) ? new Set() : new Set(foreign.items.map(i => i.product_id)))}>
                {foreignSelected.size === (foreign?.items.length || 0) && foreignSelected.size > 0 ? 'Снять выбор' : 'Выбрать все'}
              </button>
              {(() => {
                const n = Array.from(foreignSelected).filter(pid => foreignLinks[pid]).length;
                return (
                  <button className="btn btn-primary btn-sm" disabled={n === 0 || !!busy} onClick={linkForeign}>
                    {busy === 'link' ? 'Привязываем...' : `Привязать к нашим (${n})`}
                  </button>
                );
              })()}
              {(() => {
                const n = Array.from(foreignSelected).filter(pid => !foreignLinks[pid]).length;
                return (
                  <button type="button" className="btn btn-danger-outline btn-sm" disabled={n === 0 || !!busy} onClick={archiveForeign}
                    title="Только отмеченные, у которых не выбран наш букет">
                    {busy === 'archive' ? 'Архивируем...' : `В архив без пары (${n})`}
                  </button>
                );
              })()}
            </div>
          </div>
          {tabLoading && !foreign ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <div className="table-container">
              <table className="table" style={{ minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Название</th>
                    <th style={{ minWidth: 220 }}>Наш букет</th>
                    <th>Flowwow #</th>
                    <th>offerId</th>
                    <th>Тип</th>
                    <th>Подкат.</th>
                    <th>Активна</th>
                    <th>Остаток</th>
                    <th>Цена</th>
                    <th>Фото</th>
                  </tr>
                </thead>
                <tbody>
                  {(!foreign || foreign.items.length === 0) && (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: '40px 20px', color: '#5E6561' }}>Чужих карточек нет</td></tr>
                  )}
                  {foreign?.items.map(i => (
                    <tr key={i.product_id}>
                      <td><input type="checkbox" checked={foreignSelected.has(i.product_id)}
                        onChange={(e) => { const n = new Set(foreignSelected); if (e.target.checked) n.add(i.product_id); else n.delete(i.product_id); setForeignSelected(n); }} /></td>
                      <td style={{ fontWeight: 500 }}>{i.url ? <a href={i.url} target="_blank" rel="noreferrer">{i.name}</a> : i.name}</td>
                      <td>
                        {i.linkable ? (
                          <select className="form-input" style={{ fontSize: 13, padding: '4px 8px', width: '100%' }}
                            value={foreignLinks[i.product_id] || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setForeignLinks(prev => { const n = { ...prev }; if (v) n[i.product_id] = v; else delete n[i.product_id]; return n; });
                              // выбрали букет — строка отмечена; сняли пару — снимаем и отметку,
                              // иначе строка тихо уйдёт в «В архив без пары»
                              setForeignSelected(prev => { const n = new Set(prev); if (v) n.add(i.product_id); else n.delete(i.product_id); return n; });
                            }}>
                            <option value="">— не привязывать —</option>
                            {(foreign.bouquets || []).map(b => (
                              <option key={b.id} value={b.id}
                                disabled={b.linked || b.off || Object.entries(foreignLinks).some(([pid, bid]) => bid === String(b.id) && pid !== String(i.product_id))}>
                                {b.name}{b.price != null ? ` · ${Number(b.price).toLocaleString('ru-RU')} ₽` : ''}{b.linked ? ' · уже на Flowwow' : b.off ? ' · скрыт или снят с Flowwow' : !b.has_photo ? ' · нет фото' : ''}
                              </option>
                            ))}
                          </select>
                        ) : <span style={{ color: '#999', fontSize: 12 }}>не букет</span>}
                        {i.suggested_bouquet_id && String(i.suggested_bouquet_id) === foreignLinks[i.product_id] && (
                          <div style={{ fontSize: 11, color: '#4A8C6E', marginTop: 2 }}>подобрано по названию</div>
                        )}
                      </td>
                      <td>{i.product_id}</td>
                      <td><code style={{ fontSize: 12 }}>{i.offer_id || '—'}</code></td>
                      <td>{i.type === 3 ? 'доп. товар' : i.type === 2 ? 'готовый' : 'обычный'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{subcatLabel(i.sub_category_id)}</td>
                      <td>{i.is_active ? <span className="badge badge-completed">да</span> : <span className="badge badge-inactive">нет</span>}</td>
                      <td>{i.stock}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{i.price != null ? `${Number(i.price).toLocaleString('ru-RU')} ₽` : '—'}</td>
                      <td>{i.images}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!advanced && (
        <div style={{ fontSize: 12, color: '#999', margin: '6px 0 14px' }}>
          Карточки, остатки и цены уходят на Flowwow сами при каждом изменении, заказы приходят автоматически — кнопок не нужно.
        </div>
      )}

      {activeTab === 'log' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <select value={logKind} onChange={(e) => { setLogKind(e.target.value); setLog(null); }}>
              <option value="">Все записи</option>
              {Object.entries(LOG_KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          {tabLoading && !log ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <div className="table-container">
              <table className="table" style={{ minWidth: 800 }}>
                <thead>
                  <tr>
                    <th>Время</th>
                    <th>Тип</th>
                    <th>Действие</th>
                    <th>Статус</th>
                    <th>ID заказа</th>
                    <th>Flowwow #</th>
                    <th>Сообщение</th>
                    <th>Попытки</th>
                  </tr>
                </thead>
                <tbody>
                  {(!log || log.items.length === 0) && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 20px', color: '#5E6561' }}>Записей нет</td></tr>
                  )}
                  {log?.items.map(r => (
                    <tr key={r.id} style={r.status === 'error' ? { background: '#FFF4F0' } : undefined}>
                      <td style={{ whiteSpace: 'nowrap', color: '#5E6561' }}>{formatDateTime(r.created_at)}</td>
                      <td>{LOG_KIND_LABELS[r.kind] || r.kind}</td>
                      <td><code style={{ fontSize: 12 }}>{r.action}</code></td>
                      <td>{statusBadge(r.status)}</td>
                      <td>{r.order_id || '—'}</td>
                      <td>{r.external_order_id || '—'}</td>
                      <td style={{ fontSize: 12, maxWidth: 420, wordBreak: 'break-word' }}>{r.message || '—'}</td>
                      <td>{r.attempts || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 18 }}>
        <button type="button" onClick={toggleAdvanced}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
          {advanced ? 'Скрыть служебные функции' : 'Служебные функции (для разработчика платформы)'}
        </button>
      </div>
    </div>
  );
}

export default FlowwowPage;
